/**
 * /fit/walks · the Walks tab of the /fit pillar.
 *
 * Founder ruling 1 Jul 2026 (evening):
 *   "/walks is a tab on /fit · header parity is non-negotiable ·
 *    each stop card must open like every other pillar card, with
 *    a route map inside the drawer."
 *
 * Surfaces:
 *   · Identical /fit chrome (FitTopBar shared with the venues tab)
 *   · A "Venues / Walks" tab toggle at the top
 *   · One input ("any city") · four style chips · Build the loop
 *   · Returns a proximity-clustered 6-stop loop via the existing
 *     /api/walks endpoint (Distance Matrix walking-minutes per leg)
 *   · Each stop is a CLICKABLE card · opens a drawer with:
 *       — single-pin static map (StaticMapBand)
 *       — full name · address · rating · editorial summary
 *       — Reserve ↗ (Google) + Request via Concierge® CTAs
 *
 * The photo URLs returned by the backend are now proxy paths
 * (/api/places/photo?ref=...) · the AIza key never leaves the
 * server. See routes/places_photo.py.
 */
import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { TableFooter } from "@/components/table/TableFooter";
import FitTopBar from "@/components/fit/FitTopBar";
import StaticMapBand from "@/components/shared/StaticMapBand";
import PillarCard from "@/components/shared/PillarCard";

import MemberContextBand from "../components/MemberContextBand";
const API = process.env.REACT_APP_BACKEND_URL || "";

// Same /fit token map · keeps the look in lockstep with the pillar.
const T = {
  bg:    "#F4EFE6",
  card:  "#FCFAF4",
  ink:   "#211C15",
  ink2:  "#5C5447",
  ink3:  "#8A8071",
  line:  "#E5DCCB",
  line2: "#D8CDB7",
  brass: "#8B6914",
};

const STYLES = [
  { key: "default",  label: "Editor's pick",  tag: "A hand-tuned mix" },
  { key: "morning",  label: "Morning ritual", tag: "Cafés · markets · gardens" },
  { key: "evening",  label: "Evening glow",   tag: "Wine bars · rooftops · jazz" },
  { key: "heritage", label: "Heritage trail", tag: "Museums · monuments · old towns" },
];

// Resolve a backend-relative URL to a full URL for <img>.
function resolveSrc(u) {
  if (!u) return null;
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  return `${API}${u.startsWith("/") ? "" : "/"}${u}`;
}

export default function Walks() {
  const navigate = useNavigate();
  const params = useParams();           // { ref } when on /fit/walks/saved/:ref
  const location = useLocation();
  const isMineRoute = location.pathname.endsWith("/walks/mine");
  const savedRefFromUrl = params?.ref || null;
  const [city, setCity] = useState("");
  const [style, setStyle] = useState("default");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [openStop, setOpenStop] = useState(null);    // drawer · null | stop
  const [briefStop, setBriefStop] = useState(null);  // request modal · null | stop
  const [saveOpen, setSaveOpen] = useState(false);   // save-loop modal
  const [savedRef, setSavedRef] = useState(null);    // last saved ref

  const runSearch = useCallback(async (cityVal, styleVal) => {
    const target = (cityVal ?? city).trim();
    if (!target) { setError("Type any city — Lisbon, Naples, Tokyo, Mumbai…"); return; }
    setBusy(true); setError(null); setResults(null);
    try {
      const r = await fetch(`${API}/api/walks?city=${encodeURIComponent(target)}&style=${encodeURIComponent(styleVal ?? style)}&stops=6`);
      if (r.status === 404) throw new Error("The Concierge® couldn't find enough stops here. Try a bigger city or different style.");
      if (!r.ok) throw new Error("Could not reach the Concierge®. Try again in a moment.");
      const j = await r.json();
      setResults(j);
    } catch (err) {
      setError(err.message || "Unknown error");
    } finally { setBusy(false); }
  }, [city, style]);

  // ── Saved loop ── load by ref when URL is /fit/walks/saved/:ref ─
  useEffect(() => {
    if (!savedRefFromUrl) return;
    let cancelled = false;
    (async () => {
      setBusy(true); setError(null); setResults(null);
      try {
        const r = await fetch(`${API}/api/walks/saved/${encodeURIComponent(savedRefFromUrl)}`);
        if (r.status === 404) throw new Error("That saved loop has expired or the link is wrong.");
        if (!r.ok) throw new Error("Could not open the saved loop.");
        const j = await r.json();
        if (cancelled) return;
        setResults({
          city:           j.city,
          style:          j.style || "default",
          stops:          j.stops || [],
          stops_count:    j.stops_count,
          total_walk_min: j.total_walk_min,
          loop_close_min: j.loop_close_min,
          _saved_ref:     j.ref,
          _saved_note:    j.note,
        });
        setCity(j.city || "");
        setStyle(j.style || "default");
        setSavedRef(j.ref);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally { if (!cancelled) setBusy(false); }
    })();
    return () => { cancelled = true; };
  }, [savedRefFromUrl]);

  return (
    <div
      data-testid="walks-page"
      data-table-surface="fit-walks"
      style={{
        background: T.bg, color: T.ink, minHeight: "100vh",
        fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
        "--t-bg": T.bg, "--t-ink": T.ink, "--t-ink2": T.ink2,
        "--t-ink3": T.ink3, "--t-line": T.line, "--t-brass": T.brass,
      }}
    >
      <FitTopBar onHome={() => navigate("/fit")} testid="walks-header" />
      <MemberContextBand pillar="walks" />

      <main style={{ maxWidth: 1180, margin: "0 auto", padding: "12px 28px 80px" }}>
{/* Tab toggle · Venues | Walks · mirrors any-other-pillar tab pattern */}
        <div
          data-testid="walks-tab-toggle"
          style={{
            display: "flex", justifyContent: "center",
            gap: 8, margin: "20px 0 28px", flexWrap: "wrap",
          }}
        >
          <button
            type="button"
            data-testid="walks-tab-venues"
            onClick={() => navigate("/fit")}
            style={tabStyle(false)}
          >
            Venues
          </button>
          <button
            type="button"
            data-testid="walks-tab-walks"
            onClick={() => navigate("/fit/walks")}
            style={tabStyle(!isMineRoute)}
          >
            Walks
          </button>
          <button
            type="button"
            data-testid="walks-tab-mine"
            onClick={() => navigate("/fit/walks/mine")}
            style={tabStyle(isMineRoute)}
          >
            My loops
          </button>
        </div>

        {/* My loops gated view */}
        {isMineRoute && (
          <MyLoopsGate
            onOpenLoop={(ref) => navigate(`/fit/walks/saved/${ref}`)}
          />
        )}

        {!isMineRoute && (
        <>

        <div style={{ textAlign: "center", maxWidth: 720, margin: "0 auto 32px" }}>
          <p
            style={{
              fontFamily: "ui-monospace, monospace",
              fontSize: 11, letterSpacing: "0.22em",
              color: T.brass, fontWeight: 700, textTransform: "uppercase",
              margin: "0 0 12px",
            }}
          >
            Concierge® · Fit · Walks
          </p>
          <h1
            className="t-serif"
            style={{
              fontSize: 38, fontWeight: 400, margin: "0 0 14px",
              letterSpacing: "-0.01em", lineHeight: 1.08,
            }}
          >
            A walking loop in <em style={{ color: T.brass, fontStyle: "italic" }}>any city.</em>
          </h1>
          <p style={{ fontSize: 14.5, color: T.ink2, margin: 0, lineHeight: 1.55 }}>
            Tell us where you are. The Concierge® stitches together six stops you&apos;d never assemble alone — a café, a market, a viewpoint, a quiet garden — with honest walk times between each.
          </p>
        </div>

        <form
          data-testid="walks-form"
          onSubmit={(e) => { e.preventDefault(); runSearch(); }}
          style={{
            background: T.card,
            border: `1px solid ${T.line2}`,
            borderRadius: 18,
            padding: "8px 8px 8px 18px",
            display: "grid",
            gridTemplateColumns: "1fr auto",
            gap: 8,
            alignItems: "center",
            maxWidth: 760,
            margin: "0 auto 18px",
            boxShadow: "0 6px 22px rgba(33,28,21,0.04)",
          }}
        >
          <input
            data-testid="walks-city-input"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="City — Lisbon · Naples · Tokyo · Mumbai…"
            style={{
              border: 0, background: "transparent", outline: "none",
              borderRadius: 0, fontSize: 16, color: T.ink,
              padding: "10px 12px", fontFamily: "inherit",
            }}
          />
          <button
            type="submit"
            disabled={busy || !city.trim()}
            data-testid="walks-submit"
            style={{
              background: busy || !city.trim() ? T.ink3 : T.ink,
              color: T.card, border: 0, borderRadius: 14,
              padding: "12px 22px", fontSize: 14, fontWeight: 600,
              cursor: busy || !city.trim() ? "not-allowed" : "pointer",
              fontFamily: "inherit",
            }}
          >
            {busy ? "Stitching loop…" : "Build the loop"}
          </button>
        </form>

        <div
          data-testid="walks-style-chips"
          style={{
            display: "flex", justifyContent: "center",
            gap: 8, marginBottom: 32, flexWrap: "wrap",
          }}
        >
          {STYLES.map((s) => {
            const active = s.key === style;
            return (
              <button
                key={s.key}
                type="button"
                data-testid={`walks-style-${s.key}`}
                onClick={() => { setStyle(s.key); if (city.trim()) runSearch(undefined, s.key); }}
                style={{
                  background: active ? T.ink : "transparent",
                  color: active ? T.card : T.ink2,
                  border: `1px solid ${active ? T.ink : T.line2}`,
                  borderRadius: 999, padding: "8px 16px",
                  fontSize: 12.5, fontWeight: 500, cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                {s.label}
              </button>
            );
          })}
        </div>

        {error && (
          <p
            data-testid="walks-error"
            style={{ color: "#B0212C", fontSize: 13, textAlign: "center", margin: "0 0 24px" }}
          >
            {error}
          </p>
        )}

        {results && (
          <section
            data-testid="walks-results"
            style={{ display: "flex", flexDirection: "column", gap: 18, maxWidth: 1100, margin: "0 auto" }}
          >
            {/* Loop header card */}
            <div
              style={{
                background: T.card,
                borderRadius: 16,
                border: `1px solid ${T.line}`,
                padding: 22,
              }}
            >
              <p
                style={{
                  fontSize: 11, color: T.brass,
                  letterSpacing: "0.18em", textTransform: "uppercase",
                  fontWeight: 700, margin: "0 0 8px",
                  fontFamily: "ui-monospace, monospace",
                }}
              >
                The loop · {results.stops_count} stops · ~{results.total_walk_min} min walking
              </p>
              <h2
                className="t-serif"
                style={{ fontSize: 26, fontWeight: 500, margin: 0, letterSpacing: "-0.01em" }}
              >
                {results.city} · {STYLES.find((x) => x.key === results.style)?.label || results.style}
              </h2>
              <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
                <button
                  type="button"
                  data-testid="walks-save-loop"
                  onClick={() => setSaveOpen(true)}
                  style={{
                    background: T.ink, color: T.bg,
                    border: 0, borderRadius: 999,
                    padding: "10px 20px",
                    fontSize: 13, fontWeight: 600,
                    cursor: "pointer", fontFamily: "inherit",
                  }}
                >
                  ♡ Save this loop
                </button>
                {savedRef && (
                  <a
                    href={`/fit/walks/saved/${savedRef}`}
                    data-testid="walks-saved-share-link"
                    style={{
                      background: "transparent", color: T.brass,
                      border: `1.5px solid ${T.brass}`, borderRadius: 999,
                      padding: "9px 18px",
                      fontSize: 12.5, fontWeight: 500,
                      textDecoration: "none", fontFamily: "inherit",
                    }}
                  >
                    Open share link · {savedRef} ↗
                  </a>
                )}
              </div>
            </div>

            <div
              data-testid="walks-stops-grid"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                gap: 18,
              }}
            >

            {/* Stops · each a clickable card · drawer opens like /fit · /stay */}
            {(results.stops || []).map((s) => (
              <PillarCard
                key={s.place_id || s.index}
                venue={s}
                testidPrefix={`walks-stop-${s.index}`}
                onView={() => setOpenStop(s)}
                onBrief={() => setBriefStop(s)}
                briefLabel="Request via Concierge®"
                topLeftPill={
                  <span style={{
                    background: T.brass, color: T.bg,
                    padding: "3px 10px", borderRadius: 999,
                    fontFamily: "ui-monospace, monospace",
                    fontSize: 10.5, letterSpacing: "0.16em",
                    fontWeight: 700, textTransform: "uppercase",
                  }}>Stop {s.index}</span>
                }
                topRightPill={s.primary_type_label ? (
                  <span style={{
                    background: "rgba(252,250,244,0.92)", color: T.brass,
                    padding: "3px 9px", borderRadius: 999,
                    fontFamily: "ui-monospace, monospace",
                    fontSize: 10, letterSpacing: "0.14em",
                    fontWeight: 600, textTransform: "uppercase",
                    border: `1px solid ${T.brass}44`,
                  }}>{s.primary_type_label}</span>
                ) : null}
                photoBadge={s.walk_min_to_next ? (
                  <span style={{
                    background: "rgba(33,28,21,0.82)", color: T.bg,
                    padding: "4px 10px", borderRadius: 999,
                    fontFamily: "ui-monospace, monospace",
                    fontSize: 10.5, letterSpacing: "0.08em",
                    fontWeight: 600,
                  }}>↓ {s.walk_min_to_next} min walk</span>
                ) : null}
              />
            ))}
            </div>

            {results.loop_close_min && (
              <p
                style={{
                  textAlign: "center", fontSize: 12, color: T.ink3,
                  fontStyle: "italic", margin: "8px 0 0",
                }}
              >
                And ~{results.loop_close_min} min back to stop 1 if you want to close the loop.
              </p>
            )}
          </section>
        )}
        </>
        )}
      </main>

      <TableFooter slug="fit/walks" tagline="A loop is just six good decisions in a row." />

      {openStop && (
        <StopDrawer
          stop={openStop}
          city={results?.city}
          onClose={() => setOpenStop(null)}
          onRequest={() => { setBriefStop(openStop); setOpenStop(null); }}
        />
      )}

      {briefStop && (
        <WalkRequestModal
          stop={briefStop}
          city={results?.city}
          onClose={() => setBriefStop(null)}
        />
      )}

      {saveOpen && results && (
        <SaveLoopModal
          city={results.city}
          style={results.style}
          stops={results.stops}
          totalWalkMin={results.total_walk_min}
          loopCloseMin={results.loop_close_min}
          onClose={() => setSaveOpen(false)}
          onSaved={(ref) => { setSavedRef(ref); setSaveOpen(false); }}
        />
      )}
    </div>
  );
}

function tabStyle(active) {
  return {
    background: active ? T.ink : "transparent",
    color: active ? T.card : T.ink2,
    border: `1px solid ${active ? T.ink : T.line2}`,
    borderRadius: 999,
    padding: "9px 22px",
    fontSize: 13, fontWeight: 600,
    cursor: active ? "default" : "pointer",
    fontFamily: "inherit",
    letterSpacing: "0.04em",
  };
}

/* ── StopCard · clickable, parity with /fit /stay /enjoy cards ──── */
function StopCard({ stop, onOpen }) {
  const photoSrc = resolveSrc(stop.photo_url);
  return (
    <article
      data-testid={`walks-stop-${stop.index}`}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen?.(); }
      }}
      style={{
        display: "flex", gap: 18,
        background: T.card,
        borderRadius: 16,
        border: `1px solid ${T.line}`,
        padding: 18,
        cursor: "pointer",
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 12px 32px rgba(33,28,21,0.10)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
    >
      {photoSrc ? (
        <div
          style={{
            width: 120, height: 120, borderRadius: 12,
            background: `url(${photoSrc}) center/cover no-repeat`,
            flexShrink: 0,
          }}
        />
      ) : (
        <div
          style={{
            width: 120, height: 120, borderRadius: 12,
            background: `linear-gradient(135deg, ${T.line} 0%, ${T.line2} 60%, ${T.brass}33 100%)`,
            flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: "'Fraunces', 'EB Garamond', serif",
            fontSize: 30, fontWeight: 500, color: T.brass,
          }}
        >
          {(stop.name || "?").trim().charAt(0).toUpperCase()}
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p
          style={{
            fontSize: 10.5, color: T.brass,
            letterSpacing: "0.22em", textTransform: "uppercase",
            fontWeight: 700, margin: "0 0 4px",
            fontFamily: "ui-monospace, monospace",
          }}
        >
          Stop {stop.index}
        </p>
        <h3
          className="t-serif"
          style={{ fontSize: 20, fontWeight: 500, margin: "0 0 6px", letterSpacing: "-0.005em" }}
        >
          {stop.name}
        </h3>
        <p style={{ fontSize: 12.5, color: T.ink3, margin: "0 0 8px" }}>
          {stop.short_address}
          {stop.rating ? <span> · <span style={{ color: T.brass }}>★</span> {stop.rating}</span> : null}
        </p>
        {stop.editorial_summary && (
          <p
            style={{
              fontSize: 13, color: T.ink2, margin: 0,
              lineHeight: 1.55, fontStyle: "italic",
            }}
          >
            “{stop.editorial_summary.length > 160 ? stop.editorial_summary.slice(0, 160).trim() + "…" : stop.editorial_summary}”
          </p>
        )}
        {stop.walk_min_to_next && (
          <p
            style={{
              fontSize: 12, color: T.brass, margin: "10px 0 0",
              fontFamily: "ui-monospace, monospace", letterSpacing: "0.06em",
            }}
          >
            ↓ {stop.walk_min_to_next} min walk to next
          </p>
        )}
      </div>
    </article>
  );
}

/* ── StopDrawer · parity with FitDetailDrawer / StayDetailDrawer ─── */
function StopDrawer({ stop, city, onClose, onRequest }) {
  const photoSrc = resolveSrc(stop.photo_url);
  const mapsUrl = stop.place_id
    ? `https://www.google.com/maps/place/?q=place_id:${encodeURIComponent(stop.place_id)}`
    : null;
  return (
    <div
      data-testid="walks-drawer-overlay"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(20,16,11,0.55)",
        display: "flex", justifyContent: "flex-end",
        zIndex: 1000,
      }}
    >
      <aside
        data-testid="walks-drawer"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(560px, 96vw)",
          background: T.card, color: T.ink,
          height: "100%", overflowY: "auto",
          boxShadow: "-12px 0 36px rgba(0,0,0,0.22)",
          display: "flex", flexDirection: "column",
        }}
      >
        {/* Chrome */}
        <div
          style={{
            position: "sticky", top: 0, zIndex: 2,
            background: T.card,
            borderBottom: `1px solid ${T.line}`,
            padding: "14px 22px",
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}
        >
          <span
            style={{
              fontFamily: "ui-monospace, monospace",
              fontSize: 10, letterSpacing: "0.22em",
              color: T.brass, fontWeight: 600, textTransform: "uppercase",
            }}
          >
            The Loop · Stop {stop.index}
          </span>
          <button
            type="button"
            data-testid="walks-drawer-close"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "transparent", border: 0,
              fontSize: 20, cursor: "pointer", color: T.ink2,
              padding: "0 8px",
            }}
          >×</button>
        </div>

        {photoSrc && (
          <div
            data-testid="walks-drawer-hero"
            style={{
              aspectRatio: "16 / 10",
              background: `url(${photoSrc}) center/cover no-repeat`,
            }}
          />
        )}

        <div style={{ padding: "22px 26px 30px", display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Branded ribbon · parity with /fit drawer */}
          <div
            data-testid="walks-drawer-ribbon"
            style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              fontFamily: "ui-monospace, monospace",
              fontSize: 10.5, letterSpacing: "0.20em",
              color: T.brass, fontWeight: 600, textTransform: "uppercase",
              borderBottom: `1px solid ${T.line}`,
              paddingBottom: 12,
            }}
          >
            <span>✦ Through your Concierge®</span>
            <span>Concierge® · Est 1998</span>
          </div>

          <h2
            className="t-serif"
            data-testid="walks-drawer-title"
            style={{ fontSize: 30, lineHeight: 1.1, fontWeight: 500, margin: 0, color: T.ink }}
          >
            {stop.name}
          </h2>

          <div
            style={{
              fontSize: 13, color: T.ink2,
              display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
            }}
          >
            {stop.primary_type_label && (
              <span
                style={{
                  fontFamily: "ui-monospace, monospace",
                  fontSize: 10.5, letterSpacing: "0.16em",
                  color: T.brass, fontWeight: 600, textTransform: "uppercase",
                  border: `1px solid ${T.brass}55`,
                  padding: "2px 9px", borderRadius: 999,
                }}
              >{stop.primary_type_label}</span>
            )}
            {stop.short_address && <span>{stop.short_address}</span>}
            {stop.rating && (
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ color: T.brass }}>★</span>
                <strong style={{ color: T.ink }}>{Number(stop.rating).toFixed(1)}</strong>
                {stop.user_ratings_total ? (
                  <span style={{ color: T.ink3 }}>({stop.user_ratings_total.toLocaleString()})</span>
                ) : null}
              </span>
            )}
          </div>

          {/* Route map · single brass pin on this stop · parity with /fit */}
          <StaticMapBand
            lat={stop.lat}
            lng={stop.lng}
            label={`Stop ${stop.index} · on the map`}
            testid="walks-drawer-map"
          />

          {stop.editorial_summary && (
            <p
              data-testid="walks-drawer-summary"
              style={{ fontSize: 14.5, lineHeight: 1.55, color: T.ink2, margin: 0 }}
            >
              {stop.editorial_summary}
            </p>
          )}

          {stop.walk_min_to_next && (
            <p
              style={{
                fontSize: 12.5, color: T.brass,
                fontFamily: "ui-monospace, monospace",
                letterSpacing: "0.08em",
                margin: 0,
              }}
            >
              ↓ {stop.walk_min_to_next} min walk to the next stop
            </p>
          )}

          <div
            data-testid="walks-drawer-ctas"
            style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 6 }}
          >
            <button
              type="button"
              onClick={onRequest}
              data-testid="walks-drawer-request"
              style={{
                background: T.ink, color: T.card,
                border: 0, borderRadius: 999,
                padding: "14px 22px",
                fontSize: 14, fontWeight: 600,
                cursor: "pointer", fontFamily: "inherit",
                textAlign: "center",
              }}
            >
              Request via Concierge®
            </button>
            {mapsUrl && (
              <a
                href={mapsUrl}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="walks-drawer-google"
                style={{
                  background: "transparent", color: T.brass,
                  border: `1.5px solid ${T.brass}`, borderRadius: 999,
                  padding: "12px 22px",
                  fontSize: 13.5, fontWeight: 500,
                  textAlign: "center", textDecoration: "none",
                  fontFamily: "inherit", display: "block",
                  transition: "all 0.15s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = T.brass; e.currentTarget.style.color = T.bg; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = T.brass; }}
              >
                Open in Google Maps ↗
              </a>
            )}
          </div>

          <p
            style={{
              fontSize: 11.5, color: T.ink3, fontStyle: "italic",
              textAlign: "center", margin: 0, lineHeight: 1.5,
            }}
          >
            Ask your Concierge® team to arrange it · we hold the door open.
          </p>
        </div>
      </aside>
    </div>
  );
}

/* ── WalkRequestModal · routes through the same /api/fit/request
 *    endpoint used by FitRequestModal so dispatch stays unified.   */
function WalkRequestModal({ stop, city, onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [adults, setAdults] = useState(2);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [prefs, setPrefs] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setError(null);
    if (!name.trim() || (!email.trim() && !phone.trim())) {
      setError("Your name plus email or phone — so the Concierge® can reply.");
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/fit/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          venue_name:  stop.name || null,
          venue_id:    stop.place_id || null,
          city:        city || null,
          category:    "walks",
          date:        date || null,
          time:        time.trim() || null,
          adults:      Number(adults) || 1,
          preferences: prefs.trim() || `Walks loop · Stop ${stop.index}${prefs ? "\n" + prefs : ""}`,
          occasion:    `Walks · Stop ${stop.index}`,
          member_name:  name.trim(),
          member_email: email.trim() || undefined,
          member_phone: phone.trim() || undefined,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || `submit_failed (${r.status})`);
      setDone(data);
    } catch (e) {
      setError(e.message || "Could not send. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <Backdrop onClose={onClose}>
        <div data-testid="walks-request-success" style={modalStyle}>
          <div style={{ fontFamily: "ui-monospace, monospace", fontSize: 11, letterSpacing: "0.22em", color: T.brass, fontWeight: 700 }}>BY THE CONCIERGE® · WALKS</div>
          <h2 className="t-serif" style={{ fontSize: 26, fontWeight: 500, margin: "10px 0 14px", color: T.ink }}>Your brief is with the Concierge®.</h2>
          <p style={{ fontSize: 14.5, color: T.ink2, lineHeight: 1.55 }}>
            We&apos;ll be in touch on <strong style={{ color: T.ink }}>{email || phone}</strong> very shortly with the next step.
          </p>
          <div
            data-testid="walks-request-ref"
            style={{ display: "inline-block", padding: "9px 16px", background: T.card, border: `1px solid ${T.brass}`, borderRadius: 999, fontFamily: "ui-monospace, monospace", fontSize: 12, color: T.brass, fontWeight: 600, marginTop: 16 }}
          >
            {done.display_ref || done.member_reference || "REF-PENDING"}
          </div>
          <div style={{ marginTop: 22 }}>
            <button type="button" onClick={onClose} style={{ background: T.ink, color: T.card, border: 0, borderRadius: 999, padding: "10px 22px", fontSize: 13.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Close</button>
          </div>
        </div>
      </Backdrop>
    );
  }

  return (
    <Backdrop onClose={onClose}>
      <div data-testid="walks-request-modal" style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontFamily: "ui-monospace, monospace", fontSize: 11, letterSpacing: "0.22em", color: T.brass, fontWeight: 700 }}>BY THE CONCIERGE® · WALKS</div>
        <h2 className="t-serif" style={{ fontSize: 24, fontWeight: 500, margin: "10px 0 8px", color: T.ink }}>
          Request via Concierge®
        </h2>
        <p style={{ fontSize: 13.5, color: T.ink2, margin: "0 0 18px" }}>
          {stop.name} · Stop {stop.index} of the {city} loop
        </p>
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Input value={date} onChange={setDate} placeholder="Date (YYYY-MM-DD)" testid="walks-modal-date" type="date" />
            <Input value={time} onChange={setTime} placeholder="Time (e.g. 10:30)" testid="walks-modal-time" />
          </div>
          <Input value={adults} onChange={setAdults} placeholder="People" testid="walks-modal-adults" type="number" />
          <Input value={name} onChange={setName} placeholder="Your name" testid="walks-modal-name" />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Input value={email} onChange={setEmail} placeholder="Email" testid="walks-modal-email" type="email" />
            <Input value={phone} onChange={setPhone} placeholder="Phone / WhatsApp" testid="walks-modal-phone" />
          </div>
          <textarea
            data-testid="walks-modal-prefs"
            value={prefs}
            onChange={(e) => setPrefs(e.target.value)}
            placeholder="Anything else? Pace, accessibility, language…"
            rows={3}
            style={{ ...inputStyle, resize: "vertical", minHeight: 80 }}
          />
          {error && <p data-testid="walks-modal-error" style={{ color: "#9a3535", fontSize: 13, margin: 0 }}>{error}</p>}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 6 }}>
            <button type="button" onClick={onClose} style={{ background: "transparent", color: T.ink2, border: `1px solid ${T.line2}`, borderRadius: 999, padding: "10px 18px", fontSize: 13.5, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
            <button type="submit" data-testid="walks-modal-submit" disabled={busy} style={{ background: T.ink, color: T.card, border: 0, borderRadius: 999, padding: "10px 22px", fontSize: 13.5, fontWeight: 600, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.65 : 1, fontFamily: "inherit" }}>
              {busy ? "Sending…" : "Send to Concierge® →"}
            </button>
          </div>
        </form>
      </div>
    </Backdrop>
  );
}

function Backdrop({ children, onClose }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(33,28,21,0.55)",
        zIndex: 1100,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20,
      }}
    >
      {children}
    </div>
  );
}

const modalStyle = {
  background: T.bg, color: T.ink, borderRadius: 18, padding: "28px 28px 22px",
  width: "min(540px, 100%)", maxHeight: "90vh", overflowY: "auto",
  boxShadow: "0 24px 60px rgba(0,0,0,0.25)",
};

const inputStyle = {
  width: "100%", padding: "11px 14px", border: `1px solid ${T.line2}`,
  borderRadius: 12, background: T.card, color: T.ink, fontSize: 14.5,
  fontFamily: "inherit", outline: "none", boxSizing: "border-box",
};

function Input({ value, onChange, placeholder, testid, type = "text", autoFocus }) {
  return (
    <input
      type={type}
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      autoFocus={autoFocus}
      style={inputStyle}
    />
  );
}


/* ── SaveLoopModal · persists the loop to /api/walks/save ────────── */
function SaveLoopModal({ city, style, stops, totalWalkMin, loopCloseMin, onClose, onSaved }) {
  const [name, setName]   = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [note, setNote]   = useState("");
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setError(null);
    if (!email.trim() && !phone.trim()) {
      setError("Email or phone — so we can surface the loop again.");
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/walks/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          city, style, stops,
          total_walk_min:  totalWalkMin,
          loop_close_min:  loopCloseMin,
          member_name:     name.trim() || undefined,
          member_email:    email.trim() || undefined,
          member_phone:    phone.trim() || undefined,
          note:            note.trim() || undefined,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || `save_failed (${r.status})`);
      onSaved?.(data.ref);
    } catch (err) {
      setError(err.message || "Could not save.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Backdrop onClose={onClose}>
      <div data-testid="walks-save-modal" style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontFamily: "ui-monospace, monospace", fontSize: 11, letterSpacing: "0.22em", color: T.brass, fontWeight: 700 }}>BY THE CONCIERGE® · WALKS</div>
        <h2 className="t-serif" style={{ fontSize: 24, fontWeight: 500, margin: "10px 0 8px", color: T.ink }}>
          Save this loop
        </h2>
        <p style={{ fontSize: 13.5, color: T.ink2, margin: "0 0 18px" }}>
          {city} · {stops?.length || 0} stops · we&apos;ll send the link so you can come back to it on the day.
        </p>
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Input value={name} onChange={setName} placeholder="Your name (optional)" testid="walks-save-name" />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Input value={email} onChange={setEmail} placeholder="Email" testid="walks-save-email" type="email" />
            <Input value={phone} onChange={setPhone} placeholder="Phone / WhatsApp" testid="walks-save-phone" />
          </div>
          <textarea
            data-testid="walks-save-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Anything to remember? (optional · 'Anniversary morning', 'Walk after the meeting'…)"
            rows={2}
            style={{ ...inputStyle, resize: "vertical", minHeight: 64 }}
          />
          {error && <p style={{ color: "#9a3535", fontSize: 13, margin: 0 }}>{error}</p>}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 6 }}>
            <button type="button" onClick={onClose} style={{ background: "transparent", color: T.ink2, border: `1px solid ${T.line2}`, borderRadius: 999, padding: "10px 18px", fontSize: 13.5, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
            <button type="submit" data-testid="walks-save-submit" disabled={busy} style={{ background: T.ink, color: T.card, border: 0, borderRadius: 999, padding: "10px 22px", fontSize: 13.5, fontWeight: 600, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.65 : 1, fontFamily: "inherit" }}>
              {busy ? "Saving…" : "Save the loop"}
            </button>
          </div>
        </form>
      </div>
    </Backdrop>
  );
}



/* ── MyLoopsGate · email-gated rail of the member's saved loops ─── */
function MyLoopsGate({ onOpenLoop }) {
  const [email, setEmail] = useState(() => {
    try { return localStorage.getItem("lc.walks.mine.email") || ""; } catch (e) { return ""; }
  });
  const [loops, setLoops] = useState(null);
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState(null);

  async function load(e) {
    if (e) e.preventDefault();
    setError(null);
    if (!email.trim() || !email.includes("@")) {
      setError("Enter the email you saved with.");
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/walks/mine?email=${encodeURIComponent(email.trim().toLowerCase())}`);
      if (!r.ok) throw new Error(`fetch_failed_${r.status}`);
      const j = await r.json();
      setLoops(j.loops || []);
      try { localStorage.setItem("lc.walks.mine.email", email.trim().toLowerCase()); } catch (e) { /* ignore */ }
    } catch (err) {
      setError(err.message || "Could not load.");
    } finally { setBusy(false); }
  }

  // Auto-load if email was remembered.
  useEffect(() => {
    if (email && !loops && !busy) { load(); }
  }, []);

  return (
    <section data-testid="walks-mine" style={{ maxWidth: 760, margin: "20px auto 0" }}>
      <div style={{ textAlign: "center", marginBottom: 22 }}>
        <p style={{
          fontFamily: "ui-monospace, monospace",
          fontSize: 11, letterSpacing: "0.22em",
          color: T.brass, fontWeight: 700, textTransform: "uppercase",
          margin: "0 0 10px",
        }}>Members · saved loops</p>
        <h2 className="t-serif" style={{ fontSize: 30, fontWeight: 500, margin: "0 0 8px", letterSpacing: "-0.01em" }}>
          Your loops, in one place.
        </h2>
        <p style={{ fontSize: 14, color: T.ink2, margin: 0 }}>
          Enter the email you saved with — we&apos;ll surface every loop you&apos;ve kept.
        </p>
      </div>

      <form onSubmit={load} style={{
        background: T.card, border: `1px solid ${T.line2}`, borderRadius: 16,
        padding: "8px 8px 8px 18px", display: "grid",
        gridTemplateColumns: "1fr auto", gap: 8, alignItems: "center",
        boxShadow: "0 6px 22px rgba(33,28,21,0.04)", marginBottom: 22,
      }}>
        <input
          data-testid="walks-mine-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          style={{
            border: 0, background: "transparent", outline: "none",
            fontSize: 15, color: T.ink, padding: "10px 12px",
            fontFamily: "inherit",
          }}
        />
        <button
          type="submit"
          data-testid="walks-mine-submit"
          disabled={busy || !email.includes("@")}
          style={{
            background: busy ? T.ink3 : T.ink, color: T.card,
            border: 0, borderRadius: 12,
            padding: "11px 22px", fontSize: 13.5, fontWeight: 600,
            cursor: busy ? "wait" : (email.includes("@") ? "pointer" : "not-allowed"),
            fontFamily: "inherit",
          }}
        >
          {busy ? "Looking…" : "Open my loops"}
        </button>
      </form>

      {error && (
        <p data-testid="walks-mine-error" style={{ textAlign: "center", color: "#9a3535", fontSize: 13 }}>{error}</p>
      )}

      {loops && loops.length === 0 && !busy && (
        <p data-testid="walks-mine-empty" style={{ textAlign: "center", color: T.ink3, fontSize: 14, fontStyle: "italic", margin: "18px 0" }}>
          No loops saved yet under that email. Build one above and tap ♡ Save.
        </p>
      )}

      {loops && loops.length > 0 && (
        <div data-testid="walks-mine-list" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {loops.map((l) => (
            <button
              key={l.ref}
              type="button"
              data-testid={`walks-mine-row-${l.ref}`}
              onClick={() => onOpenLoop?.(l.ref)}
              style={{
                textAlign: "left", background: T.card,
                border: `1px solid ${T.line}`, borderRadius: 14,
                padding: "14px 18px", cursor: "pointer", fontFamily: "inherit",
                transition: "all 0.12s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.brass; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.line; }}
            >
              <div style={{
                fontFamily: "ui-monospace, monospace",
                fontSize: 10, color: T.brass,
                letterSpacing: "0.18em", textTransform: "uppercase",
                fontWeight: 700, marginBottom: 4,
              }}>
                {l.ref} · {new Date(l.created_at).toLocaleDateString()}
              </div>
              <h3 className="t-serif" style={{ fontSize: 18, margin: 0, fontWeight: 500, color: T.ink }}>
                {l.city} · {STYLES.find((s) => s.key === l.style)?.label || l.style}
              </h3>
              <p style={{ fontSize: 12.5, color: T.ink3, margin: "4px 0 0" }}>
                {l.stops_count} stops{l.total_walk_min ? ` · ~${l.total_walk_min} min walking` : ""}
                {l.note ? ` · "${l.note}"` : ""}
              </p>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

